/**
 * Site-wide language catalog.
 *
 * Flip `status` from 'coming_soon' → 'live' when a course ships in the app.
 * Marketing copy should prefer the helpers below so pages stay in sync.
 */

export const languages = [
  {
    name: 'Hindi',
    code: 'hi',
    status: 'live',
    detail: 'Familiar Hindi for family, travel, and everyday life.',
    accent: '#1FA46A',
    background: '#E9F8F0',
    script: 'Devanagari',
  },
  {
    name: 'Gujarati',
    code: 'gu',
    status: 'live',
    detail: 'Family phrases, everyday talk, and real conversation practice.',
    accent: '#FF9440',
    background: '#FFF1E7',
    script: 'Gujarati',
  },
  {
    name: 'Punjabi',
    code: 'pa',
    status: 'live',
    detail: 'Family gatherings, village visits, and everyday Punjabi talk.',
    accent: '#7C3AED',
    background: '#F1ECFE',
    script: 'Gurmukhi',
  },
  {
    name: 'Marathi',
    code: 'mr',
    status: 'live',
    detail: 'Family talk, Mumbai life, and everyday Marathi.',
    accent: '#0D9488',
    background: '#E4F5F2',
    script: 'Devanagari',
  },
  {
    name: 'Bengali',
    code: 'bn',
    status: 'live',
    detail: 'Home conversations, festivals, and everyday Bangla.',
    accent: '#B42318',
    background: '#F9EDED',
    script: 'Bengali',
  },
  {
    name: 'Malayalam',
    code: 'ml',
    status: 'live',
    detail: 'Kerala visits, phone calls, and community life.',
    accent: '#1689F2',
    background: '#EAF5FF',
    script: 'Malayalam',
  },
  {
    name: 'Kannada',
    code: 'kn',
    status: 'live',
    detail: 'Family conversations, Bengaluru life, and everyday Kannada.',
    accent: '#D97706',
    background: '#FFF6E5',
    script: 'Kannada',
  },
  {
    name: 'Tamil',
    code: 'ta',
    status: 'live',
    detail: 'Family life, travel, and everyday Tamil conversation.',
    accent: '#BE185D',
    background: '#FCECF3',
    script: 'Tamil',
  },
  {
    name: 'Telugu',
    code: 'te',
    status: 'live',
    detail: 'Home conversations, family visits, and everyday Telugu.',
    accent: '#2563EB',
    background: '#EAF1FF',
    script: 'Telugu',
  },
  {
    name: 'Odia',
    code: 'or',
    status: 'live',
    detail: 'Family talk, Odisha visits, and everyday Odia.',
    accent: '#C2410C',
    background: '#FDEEE6',
    script: 'Odia',
  },
  {
    name: 'Urdu',
    code: 'ur',
    status: 'live',
    detail: 'Family conversations, Pakistani culture, and everyday Urdu.',
    accent: '#0F766E',
    background: '#E7F6F3',
    script: 'Nastaliq',
  },
  {
    name: 'Nepali',
    code: 'ne',
    status: 'live',
    detail: 'Family talk, Nepal travel, and everyday Nepali.',
    accent: '#DC2626',
    background: '#FEECEC',
    script: 'Devanagari',
  },
];

export const liveLanguages = languages.filter((language) => language.status === 'live');
export const comingSoonLanguages = languages.filter(
  (language) => language.status === 'coming_soon'
);

/** Human-readable list of the currently live courses. */
export function formatLanguageList(items, { conjunction = 'and' } = {}) {
  const names = items.map((item) => (typeof item === 'string' ? item : item.name));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} ${conjunction} ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, ${conjunction} ${names[names.length - 1]}`;
}

/** SEO-friendly list of the currently live courses (no Oxford comma). */
export function formatLanguageListAmpersand(items) {
  const names = items.map((item) => (typeof item === 'string' ? item : item.name));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

export const liveLanguageNames = liveLanguages.map((language) => language.name);
export const liveLanguagesPhrase = formatLanguageList(liveLanguages);
export const liveLanguagesSeoPhrase = formatLanguageListAmpersand(liveLanguages);
export const liveScriptsPhrase = formatLanguageList(
  [...new Set(liveLanguages.map((language) => language.script))]
);

export const DEFAULT_SEO_TITLE = `Bhol - Learn ${liveLanguagesSeoPhrase} | Practice Real Conversations`;
export const DEFAULT_SEO_DESCRIPTION = `Learn ${liveLanguagesPhrase} with short lessons, pronunciation practice, and conversation practice for everyday life.`;

/** Beta / interest form options: live first, then coming soon, then Other */
export const languageInterestOptions = [
  ...liveLanguageNames,
  ...comingSoonLanguages.map((language) => language.name),
  'Other',
].filter((name, index, all) => all.indexOf(name) === index);
